import { ICollectionResponse } from "../../models/ICollectionResponse";
import api, {LIST, WORKSPACES_TYPE, WORKSPACE_PRODUCTS_TYPE} from "../Api";
import {
    IAdHocWorkspaceItem,
    IWorkspace,
    IWorkspaceItem,
    IWorkspaceItemBundleSelection,
    IWorkspaceItemDetails,
    IWorkspaceItemGroupingUpdateRequest, IWorkspaceItemGroupSortUpdateRequest,
    IWorkspaceItemSortOrderUpdateRequest
} from "../../models/Workspace";
import { IActionResponse } from "../../dal/BaseDAL";
import { NormalizeResult } from "../../logic/Product";
import { cloneDeep } from "lodash";
import {INormalizedProduct} from "../../models/NormalizedProduct";
import {IProductReference} from "../../models/Extension";

export const workspaceItemsEndpoints = api.injectEndpoints({
    endpoints: (build) => ({
        // Workspace Products
        
        
        
        updateWorkspaceItemSortOrder: build.mutation<IActionResponse, { workspaceId: string, req: IWorkspaceItemSortOrderUpdateRequest }>({
            query: (arg) => {
                return {
                    url: `workspaces/${arg.workspaceId}/items/sortOrder`,
                    method: 'PATCH',
                    body: arg.req,
                }
            },
            async onQueryStarted(arg, {dispatch, queryFulfilled}) {

                const updateResult = dispatch(
                    api.util.updateQueryData('getWorkspaceProducts', arg.workspaceId, draft => {
                        // Create a mapping of item IDs to their current data in the draft
                        const itemMap = new Map(draft.map(item => [item.id, item]));

                        // Build the reordered array in exactly the requested sequence,
                        // assigning new sortOrder values so the cache reflects the new order.
                        // Do NOT re-sort by old sortOrder — that would undo the reorder.
                        const reorderedItems = arg.req.orderedWorkspaceItemIds
                            .map((id, index) => {
                                const item = itemMap.get(id);
                                if (!item) return null;
                                return { ...item, sortOrder: index + 1 } as IWorkspaceItem;
                            })
                            .filter((item): item is IWorkspaceItem => item !== null);

                        // Append any items not present in the ordered list (safety net)
                        const orderedSet = new Set(arg.req.orderedWorkspaceItemIds);
                        const extras = draft.filter(item => !orderedSet.has(item.id));

                        // Replace the draft contents in-place so Immer tracks the change correctly
                        draft.splice(0, draft.length, ...[...reorderedItems, ...extras] as IWorkspaceItem[]);
                    })
                );
                try {
                    await queryFulfilled;
                } catch (error) {
                    updateResult.undo();
                    console.log(error);
                }
            }
        }),
        updateWorkspaceItemGrouping: build.mutation<IActionResponse, { workspaceId: string, req: IWorkspaceItemGroupingUpdateRequest }>({
            query: (arg) => {
                return {
                    url: `workspaces/${arg.workspaceId}/items/grouping`,
                    method: 'PATCH',
                    body: arg.req,
                }
            },
            async onQueryStarted(arg, {dispatch, queryFulfilled}) {
                const patchResult = dispatch(
                    api.util.updateQueryData('getWorkspaceProducts', arg.workspaceId, draft => {

                        const copy = cloneDeep(draft);
                        arg.req.groupings?.forEach((g) => {
                            g.workspaceItemIds.forEach((id) => {
                                const item = copy.find(i => i.id === id);
                                if(item) {
                                    item.groupId = g.groupId;
                                    item.groupName = g.groupName;
                                }
                            })
                        })

                        Object.assign(draft, copy);
                    })
                );
                try {
                    await queryFulfilled
                } catch (error){
                    patchResult.undo()
                    console.log(error)
                }
            }
        }),
        updateWorkspaceItemGroupingSort: build.mutation<IActionResponse, { workspaceId: string, req: IWorkspaceItemGroupSortUpdateRequest }>({
            query: (arg) => {
                return {
                    url: `workspaces/${arg.workspaceId}/items/groupSort`,
                    method: 'POST',
                    body: arg.req,
                }
            },
            async onQueryStarted(arg, {dispatch, queryFulfilled}) {
                const patchResult = dispatch(
                    api.util.updateQueryData('getWorkspaceProducts', arg.workspaceId, draft => {

                        const copy = cloneDeep(draft);
                        
                        arg.req.updates.forEach((g) => {
                            const item = copy.find(i => i.id === g.workspaceItemId);
                            if(item) {
                                item.groupId = g.groupId;
                                item.groupName = g.groupName;
                                item.sortOrder = g.sortOrder;
                            }
                        })


                        Object.assign(draft, copy);
                    })
                );
                try {
                    await queryFulfilled
                } catch (error){
                    patchResult.undo()
                    console.log(error)
                }
            }
        }),
        convertUserAddedItem: build.mutation<IActionResponse, { workspaceId: string, itemId: string }>({
            query: (arg) => {
                return {
                    url: `workspaces/${arg.workspaceId}/items/${arg.itemId}/convertUserAdded`,
                    method: 'POST',
                }
            },
            async onQueryStarted(arg, {dispatch, queryFulfilled}) {

                const updateResults= await queryFulfilled;
                dispatch(
                    api.util.updateQueryData('getWorkspaceProducts', arg.workspaceId, draft => {

                        if(!updateResults.data.id) return;
                        
                        const copy = cloneDeep(draft);
                        const existingIndex = copy.findIndex(i => i.id === arg.itemId);
                        if(existingIndex === -1) return;
                        
                        const checkItem = copy.find(i => i.id === updateResults.data.id)
                        if(checkItem){
                            if(checkItem.details){
                                checkItem.details.quantity = (checkItem.details.quantity ?? 1) + 1
                            }
                            else{
                                checkItem.details = {
                                    quantity: 1
                                }
                            }
                            copy.splice(existingIndex, 1)
                        }
                        
                        else{
                            const copyItem = copy[existingIndex];
                            if(!copyItem) return;
                            copyItem.id = updateResults.data.id;
                            copyItem.userAdded = false;
                        }
                        
                        Object.assign(draft, copy);
                    })
                );
            }
        }),
        addProductToWorkspace: build.mutation<IActionResponse, { workspace: string, product: INormalizedProduct, req: IWorkspaceItemDetails }>({
            query: (arg) => {
                return {
                    url: `catalog/${arg.product.catalog}/products/${arg.product.id}/addToWorkspace/${arg.workspace}`,
                    method: 'POST',
                    body: arg.req,
                }
            },
            invalidatesTags: (_result, _error, arg) => [
                {type: WORKSPACES_TYPE, id: arg.workspace},
                {type: WORKSPACE_PRODUCTS_TYPE, id: LIST},
            ],
            async onQueryStarted(arg, {dispatch, queryFulfilled}) {
                try {
                    await queryFulfilled
                    dispatch(
                        workspaceItemsEndpoints.util.updateQueryData('getWorkspace', arg.workspace, draft => {
                            draft.documentIsCurrent = false
                        })
                    )
                } catch (error){
                    console.log(error)
                }
            },
        }),
        importWorkspaceProductsFromTemplate: build.mutation<IWorkspaceItem[], {sourceId: string, destinationId: string}>({
            query: (arg) => {
                return {
                    url: `workspaces/${arg.destinationId}/addItemsFromTemplate/${arg.sourceId}`,
                    method: 'POST'
                }
            },
            transformResponse: (response: ICollectionResponse<IWorkspaceItem>) => response.items.map(p => ({
                ...p,
                normalized: p.product ? NormalizeResult(p.product) : undefined
            })),
            async onQueryStarted(arg, {dispatch, queryFulfilled}) {
                try {
                    const finishedData = await queryFulfilled
                    dispatch(
                        api.util.updateQueryData('getWorkspaceProducts', arg.destinationId, (draft) => {
                            finishedData.data.forEach((a) => {
                                draft.push(a)
                            })
                        })
                    )
                    dispatch(
                        api.util.updateQueryData('getWorkspace', arg.destinationId, draft => {
                            draft.documentIsCurrent = false
                        }))
                } catch (error){
                    console.log(error)
                }
            },
        }),
        addProductReferenceToWorkspace: build.mutation<IActionResponse, { workspace: string, product: IProductReference, req: IWorkspaceItemDetails }>({
            query: (arg) => ({
                url: `catalog/${arg.product.catalog}/products/${arg.product.id}/addToWorkspace/${arg.workspace}`,
                method: 'POST',
                body: arg.req,
            }),
            invalidatesTags: (_result, _error, arg) => [
                {type: WORKSPACES_TYPE, id: arg.workspace},
                {type: WORKSPACE_PRODUCTS_TYPE, id: LIST},
            ],
        }),
        removeMultipleProductsFromWorkspace: build.mutation<IActionResponse, { workspace: IWorkspace, products: string[] }>({
            query: (arg) => ({
                url: `workspaces/${arg.workspace.id}/items`,
                method: 'DELETE',
                body: {
                    items: arg.products
                }
            }),
            async onQueryStarted(arg, {dispatch, queryFulfilled}) {
                const patchResult = dispatch(
                    api.util.updateQueryData('getWorkspaceProducts', arg.workspace.id, (draft) => {
                        arg.products.forEach((a) => {
                            const i = draft.findIndex(p => p.id === a);
                            if (i !== -1) {
                                draft.splice(i, 1)
                            }
                        })
                    })
                )
                const workspacePatch = dispatch(
                    api.util.updateQueryData('getWorkspace', arg.workspace.id, draft => {
                        draft.documentIsCurrent = false
                    }))
                try {
                    await queryFulfilled
                } catch (error){
                    patchResult.undo()
                    workspacePatch.undo()
                    console.log(error)
                }
            },
        }),
        removeProductFromWorkspace: build.mutation<IActionResponse, { workspaceId: string, wsItemId: string }>({
            query: (arg) => ({
                url: `workspaces/${arg.workspaceId}/items/${arg.wsItemId}`,
                method: 'DELETE'
            }),
            async onQueryStarted(arg, {dispatch, queryFulfilled}) {
                const patchResult = dispatch(
                    api.util.updateQueryData('getWorkspaceProducts', arg.workspaceId, (draft) => {
                        const i = draft.findIndex(p => p.id === arg.wsItemId);
                        if (i === -1) {console.log("no product found"); return}
                        draft.splice(i, 1)
                    })
                )
                const workspacePatch = dispatch(
                    api.util.updateQueryData('getWorkspace', arg.workspaceId, draft => {
                        draft.documentIsCurrent = false
                    }))
                try {
                    await queryFulfilled
                } catch (error){
                    patchResult.undo()
                    workspacePatch.undo()
                    console.log(error)
                }
            },
            // The optimistic splice above updates the grid instantly, but anything that re-estimates
            // off the item list (workspace totals) must run against the server's state after the
            // delete lands, not before.
            invalidatesTags: (_result, _error, arg) => [
                {type: WORKSPACES_TYPE, id: arg.workspaceId},
                {type: WORKSPACE_PRODUCTS_TYPE, id: LIST},
            ]
        }),
        putWorkspaceItemDetails: build.mutation<IActionResponse, { workspace: string, product: string, details: IWorkspaceItemDetails }>({
            query: (arg) => ({
                url: `workspaces/${arg.workspace}/items/${arg.product}`,
                method: 'PUT',
                body: arg.details
            }),
            async onQueryStarted(arg, {dispatch, queryFulfilled}) {
                const patchResult = dispatch(
                    api.util.updateQueryData('getWorkspaceProducts', arg.workspace, (draft) => {
                        const products = cloneDeep(draft);
                        const i = products.findIndex(p => p.id === arg.product);
                        if (i === -1) {console.log("no product found"); return}
                        const prod = products[i]
                        prod.details = arg.details;
                        Object.assign(draft, products)
                    })
                )
                const workspacePatch = dispatch(
                    api.util.updateQueryData('getWorkspace', arg.workspace, draft => {
                        draft.documentIsCurrent = false
                    }))
                try {
                    await queryFulfilled
                } catch (error) {
                    console.log(error)
                    patchResult.undo()
                    workspacePatch.undo()
                }
            },
        }),
        addAdhocProductToWorkspace: build.mutation<IWorkspaceItem, { workspace: string, req: IAdHocWorkspaceItem }>({
            query: (arg) => {
                return {
                    url: `workspaces/${arg.workspace}/items/addAdhoc`,
                    method: 'POST',
                    body: arg.req,
                }
            },
            invalidatesTags: (_result, _error, arg) => [
                {type: WORKSPACES_TYPE, id: arg.workspace},
                {type: WORKSPACE_PRODUCTS_TYPE, id: LIST},
            ],
            async onQueryStarted(arg, {dispatch, queryFulfilled}) {
                try {
                    await queryFulfilled
                    dispatch(
                        api.util.updateQueryData('getWorkspace', arg.workspace, draft => {
                            draft.documentIsCurrent = false
                        })
                    )
                } catch (error){
                    console.log(error)
                }
            },
        }),
        updateWorkspaceItemPreparedConfiguration: build.mutation<IActionResponse, { workspaceId: string, wsItemId: string, configuration: IWorkspaceItemBundleSelection[] }>({
            query: (arg) => ({
                url: `workspaces/${arg.workspaceId}/items/${arg.wsItemId}/preparedConfiguration`,
                method: 'PUT',
                body: arg.configuration,
            }),
            async onQueryStarted(arg, {dispatch, queryFulfilled}) {
                const patchResult = dispatch(
                    api.util.updateQueryData('getWorkspaceProducts', arg.workspaceId, (draft) => {
                        const products = cloneDeep(draft);
                        const i = products.findIndex(p => p.id === arg.wsItemId);
                        if (i === -1) return;
                        products[i].preparedConfiguration = arg.configuration;
                        Object.assign(draft, products);
                    })
                );
                try {
                    await queryFulfilled;
                } catch (error) {
                    patchResult.undo();
                    console.log(error);
                }
            },
        }),
        putWorkspaceAdhocItem: build.mutation<IActionResponse, { workspace: string, adhocItem: string, details: IAdHocWorkspaceItem }>({
            query: (arg) => ({
                url: `workspaces/${arg.workspace}/items/adhoc/${arg.adhocItem}`,
                method: 'PUT',
                body: arg.details
            }),
            async onQueryStarted(arg, {dispatch, queryFulfilled}) {
                const patchResult = dispatch(
                    api.util.updateQueryData('getWorkspaceProducts', arg.workspace, (draft) => {
                        const products = cloneDeep(draft);
                        const i = products.findIndex(p => p.id === arg.adhocItem);
                        if (i === -1) {console.log("no product found"); return}
                        const prod = products[i]
                        prod.adhoc = arg.details;
                        Object.assign(draft, products)
                    })
                )
                const workspacePatch = dispatch(
                    api.util.updateQueryData('getWorkspace', arg.workspace, draft => {
                        draft.documentIsCurrent = false
                    }))
                try {
                    await queryFulfilled
                } catch (error) {
                    console.log(error)
                    patchResult.undo()
                    workspacePatch.undo()
                }
            },
        }),
        
        
    }),
    overrideExisting: false,
});

export const {
    useGetWorkspaceProductsQuery,
    useUpdateWorkspaceItemSortOrderMutation,
    useUpdateWorkspaceItemGroupingMutation,
    useConvertUserAddedItemMutation,
    useAddProductToWorkspaceMutation,
    useImportWorkspaceProductsFromTemplateMutation,
    useAddProductReferenceToWorkspaceMutation,
    useRemoveMultipleProductsFromWorkspaceMutation,
    useRemoveProductFromWorkspaceMutation,
    usePutWorkspaceItemDetailsMutation,
    useAddAdhocProductToWorkspaceMutation,
    usePutWorkspaceAdhocItemMutation,
    useUpdateWorkspaceItemGroupingSortMutation,
    useUpdateWorkspaceItemPreparedConfigurationMutation
} = workspaceItemsEndpoints;